
import React from 'react';
import { Download } from 'lucide-react';
import { AdverseReactions, ClinicalDetails } from '@/data/medications';
import AdverseReactionsSection from './AdverseReactionsSection';
import { Button } from '@/components/ui/button';
import { toast } from "@/components/ui/use-toast";

interface ClinicalDetailsSectionProps {
  clinicalDetails?: ClinicalDetails;
  adverseReactions?: AdverseReactions;
  medicationName?: string;
}

const ClinicalDetailsSection: React.FC<ClinicalDetailsSectionProps> = ({ 
  clinicalDetails, 
  adverseReactions,
  medicationName
}) => {
  if (!clinicalDetails && !adverseReactions) return null;
  
  const handleDownloadReport = () => {
    if (!medicationName || !clinicalDetails) return;
    
    try {
      // Create text content for download
      const textContent = `
CLINICAL REPORT: ${medicationName.toUpperCase()}
Generated: ${new Date().toLocaleDateString()}

CLINICAL DETAILS
---------------
Dosing Guidelines:
${clinicalDetails.dosingGuidelines}

Administration Protocols:
${clinicalDetails.administrationProtocols}

Contraindications:
${clinicalDetails.contraindications}

Monitoring Requirements:
${clinicalDetails.monitoringRequirements}
${adverseReactions ? `
ADVERSE REACTIONS
---------------
Common:
${adverseReactions.common}

Less Common:
${adverseReactions.lessCommon}

Rare:
${adverseReactions.rare}
` : ''}

For clinical use only. Consult full prescribing information before use.
AutoPilot Care - Page 1 of 1
      `;
      
      // Create a blob and download
      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${medicationName}_Clinical_Report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: "Report Downloaded",
        description: `${medicationName} clinical report has been downloaded.`,
        duration: 3000,
      });
    } catch (error) {
      console.error("Report generation error:", error);
      toast({
        title: "Error",
        description: "Failed to generate report. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    }
  };
  
  return (
    <div className="grid gap-4">
      {clinicalDetails && (
        <>
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-primary">Clinical Details</span>
              <Button 
                variant="outline" 
                size="sm" 
                className="h-7 px-2 ml-2 text-xs flex items-center gap-1" 
                onClick={handleDownloadReport}
                title="Download Report"
              >
                <Download className="h-3.5 w-3.5" />
                <span>Report</span>
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-3 text-xs md:text-sm">
            <div className="p-3 bg-blue-50 rounded-md border border-blue-100">
              <span className="font-medium text-blue-700 block mb-1">Dosing Guidelines:</span>
              <span>{clinicalDetails.dosingGuidelines}</span>
            </div>
            
            <div className="p-3 bg-green-50 rounded-md border border-green-100">
              <span className="font-medium text-green-700 block mb-1">Administration Protocols:</span>
              <span>{clinicalDetails.administrationProtocols}</span>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-md border border-amber-100">
              <span className="font-medium text-amber-700 block mb-1">Contraindications:</span>
              <span>{clinicalDetails.contraindications}</span>
            </div>
            
            <div className="p-3 bg-purple-50 rounded-md border border-purple-100">
              <span className="font-medium text-purple-700 block mb-1">Monitoring Requirements:</span>
              <span>{clinicalDetails.monitoringRequirements}</span>
            </div>
          </div>
        </>
      )}
      
      {adverseReactions && <AdverseReactionsSection adverseReactions={adverseReactions} />}
    </div>
  );
};

export default ClinicalDetailsSection;
